import React from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { getPeriodComparisonLabel } from '../../utils/periodLabels';
import InfoTip from '../ui/InfoTip';

/**
 * Compact KPI tile used inside the Flux / Encours blocks.
 * Self-contained (no carousel StatTile) so it stays dense like the mockup.
 *
 * The comparison label ("vs la semaine/mois/... précédent(e)") is derived from
 * the selected period type via getPeriodComparisonLabel — never passed in — so
 * every tile with a comparison shows the precise wording.
 *
 * Delta rules:
 *  - omit `previousValue` (undefined) → no comparison line (used by derived tiles
 *    like "Solde net" / "Total ouverts" which carry an explanatory `additionalInfo`).
 *  - previousValue === 0 or null → "Pas de base comparable" (never "+∞").
 */
const formatValue = (val) => {
  if (typeof val === 'number') {
    return Math.abs(val) >= 1000 ? val.toLocaleString('fr-FR') : `${val}`;
  }
  return val;
};

const GPSKpiTile = ({ label, value, previousValue, icon, description = null, additionalInfo = null, className = '', fill = null }) => {
  const gps = useGPS();
  const comparisonLabel = getPeriodComparisonLabel(gps.periodType);

  // Filled (colored) variant — used by synthesis tiles (Solde net, Total ouverts).
  // No delta line, light text on a colored gradient.
  if (fill) {
    return (
      <div className={`relative rounded-xl p-3 h-full text-left ${className}`} style={{ background: fill.gradient }}>
        <div className="flex items-center gap-1.5" style={{ color: fill.labelColor }}>
          {icon && (
            <span className="flex-shrink-0" aria-hidden>
              {React.cloneElement(icon, { className: 'w-4 h-4', strokeWidth: '2' })}
            </span>
          )}
          <span className="text-[13px] font-semibold truncate">{label}</span>
          {description && (
            <InfoTip text={description} className="ml-auto shrink-0" dotClassName="border-white/40 text-white/80 hover:border-white/70 hover:text-white" />
          )}
        </div>
        <div className="mt-1.5 text-2xl font-extrabold leading-none tracking-tight" style={{ color: fill.valueColor }}>
          {formatValue(value)}
        </div>
        {additionalInfo && (
          <div className="mt-1.5 text-[11px] leading-snug" style={{ color: fill.infoColor }}>
            {additionalInfo}
          </div>
        )}
      </div>
    );
  }

  const hasComparison = previousValue !== undefined;
  const prev = typeof previousValue === 'number' ? previousValue : null;
  const cur = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^\d.-]/g, ''));
  const noComparableBase = hasComparison && (prev === null || prev === 0);

  let deltaNode = null;
  if (hasComparison && !noComparableBase && !Number.isNaN(cur)) {
    const diff = cur - prev;
    const pct = ((cur - prev) / Math.abs(prev)) * 100;
    const tone = pct > 0 ? 'text-emerald-600' : pct < 0 ? 'text-red-600' : 'text-gray-500';
    const sign = pct > 0 ? '+' : '';
    deltaNode = (
      <div className="mt-2 text-left">
        <span className={`text-xs font-bold ${tone}`}>
          {sign}{pct.toFixed(1)}%{' '}
          <span className="font-semibold">({diff > 0 ? '+' : ''}{Math.round(diff).toLocaleString('fr-FR')})</span>
        </span>
        <div className="text-[11px] text-gray-500 dark:text-gray-400">{comparisonLabel}</div>
      </div>
    );
  }

  const infoNode = (noComparableBase ? 'Pas de base comparable' : additionalInfo);

  return (
    <div className={`relative bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-900 rounded-xl p-3 h-full text-left ${className}`}>
      <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
        {icon && (
          <span className="flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden>
            {React.cloneElement(icon, { className: 'w-4 h-4', strokeWidth: '2' })}
          </span>
        )}
        <span className="text-[13px] font-semibold truncate">{label}</span>
        {description && <InfoTip text={description} className="ml-auto shrink-0" />}
      </div>
      <div className="mt-1.5 text-2xl font-extrabold leading-none tracking-tight text-gray-900 dark:text-gray-50">
        {formatValue(value)}
      </div>
      {deltaNode}
      {infoNode && <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">{infoNode}</div>}
    </div>
  );
};

export default GPSKpiTile;
